import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { formatInteger, formatTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, FilterChip, SearchField, Segmented, useToast } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, api, errorMessage } from '../../../lib/api';
import {
  dealKeys,
  requestDealsWriteAccess,
  useBoard,
  useDealFilters,
  useInvalidateDeals,
  usePipeline,
} from '../api';
import { ReceivedDialog } from '../invoicing/InvoiceDialogs';
import { HistoryPanel } from '../dialogs/HistoryPanel';
import { LoseDialog } from '../dialogs/LoseDialog';
import { StageDialog } from '../dialogs/StageDialog';
import { activeFilterCount } from '../filters';
import { ageText, dealsCount, sumWithEuro } from '../format';
import type {
  BoardCard,
  BoardResponse,
  DealFilter,
  InvoiceRow,
  InvoicesResponse,
  PipelineInfo,
} from '../types';
import { DealsTabs, type DealsTab } from '../DealsTabs';
import { BoardSkeleton } from './BoardSkeleton';
import { FilterFlyout } from './FilterFlyout';
import { FinancingBoard } from '../../financings/board/FinancingBoard';
import { LockedPipeline } from './LockedPipeline';
import { PipelineBoard } from './PipelineBoard';
import type { CardActions } from './BoardCard';
import pageStyles from '../DealsPage.module.css';
import styles from './board.module.css';

/**
 * Deal-Pipeline 01–16 + 17–23: the pipeline tab of Tools › Deals — head row with the filter pills, the
 * summary, the search and the late-phase toggle; the banners of the filtered views; the board with its
 * loading, error, empty, no-hit, read-only and locked states; the stage/lost dialogs and the history
 * panel opened from the cards.
 */

export interface PipelineTabProps {
  filter: DealFilter;
  onFilterChange: (filter: DealFilter) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  pipelineKind: 'property' | 'financing';
  onPipelineKindChange: (kind: 'property' | 'financing') => void;
  onTabChange: (tab: DealsTab) => void;
  onCreate: () => void;
}

type DialogState =
  | { kind: 'stage' | 'lose' | 'history'; card: BoardCard; targetStageId: string | null }
  | { kind: 'received'; card: BoardCard; row: InvoiceRow };

/** The after-sales stage the receipt hands over to (Deal-Pipeline 22 „Übergabe an"). */
function handoverOf(
  info: PipelineInfo | undefined,
): { stageName: string; responsibleLabel: string | null } | null {
  if (!info || info.locked) return null;
  const stage =
    info.stages.find((entry) => entry.isAfterSalesHandover) ?? info.stages[info.stages.length - 1];
  return stage ? { stageName: stage.name, responsibleLabel: stage.responsibleLabel } : null;
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function summaryText(data: BoardResponse, filter: DealFilter, expandedCancelled: boolean): string {
  const summary = data.summary;
  if (filter.q && summary.filteredDeals === 0) return '0 Treffer';
  if (filter.gateOnly)
    return `${dealsCount(summary.filteredDeals)} · ${sumWithEuro(summary.filteredValueCents)} blockiert`;
  if (filter.overdueOnly) {
    const oldest = summary.oldestAgeDays === null ? '' : ` · ältester ${ageText(summary.oldestAgeDays)}`;
    return `${dealsCount(summary.filteredDeals)} über Frist${oldest}`;
  }
  if (summary.filtered) {
    return `${formatInteger(summary.filteredDeals)} von ${formatInteger(summary.deals)} Deals · ${sumWithEuro(summary.filteredValueCents)}`;
  }
  if (expandedCancelled && data.cancelledColumn) {
    return `${dealsCount(summary.deals)} · ${formatInteger(data.cancelledColumn.count)} storniert`;
  }
  if (summary.deals === 0) return '0 Deals · 0 €';
  if (summary.valueCents === null) return `${dealsCount(summary.deals)} · Summen ausgeblendet`;
  return `${dealsCount(summary.deals)} · ${sumWithEuro(summary.valueCents)} KP`;
}

function overdueBanner(data: BoardResponse): string | null {
  let best: { card: BoardCard; stage: string; deadline: number | null } | null = null;
  for (const column of data.columns) {
    for (const card of column.cards) {
      if (!card.overdue) continue;
      if (!best || card.ageDays > best.card.ageDays)
        best = { card, stage: column.name, deadline: column.deadlineDays };
    }
  }
  if (!best) return null;
  const deadline = best.deadline === null ? '' : ` · Frist ${formatInteger(best.deadline)} Tage`;
  return `${best.card.contactName} · ${formatInteger(best.card.ageDays)} Tage in „${best.stage}“${deadline}`;
}

export function PipelineTab({
  filter,
  onFilterChange,
  expanded,
  onExpandedChange,
  pipelineKind,
  onPipelineKindChange,
  onTabChange,
  onCreate,
}: PipelineTabProps) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const [search, setSearch] = useState(filter.q ?? '');
  const debouncedSearch = useDebounced(search.trim(), 350);
  useEffect(() => {
    if (debouncedSearch === (filter.q ?? '')) return;
    onFilterChange({ ...filter, q: debouncedSearch || undefined });
    // the URL follows the typed search
  }, [debouncedSearch]);
  useEffect(() => {
    setSearch(filter.q ?? '');
  }, [filter.q]);

  const property = pipelineKind === 'property';
  const queryClient = useQueryClient();
  const board = useBoard(filter, expanded ? 'expanded' : 'collapsed', property);
  const filters = useDealFilters(property);
  const pipeline = usePipeline(board.data?.pipeline.id ?? null, property && board.data !== undefined);
  const filterButtonRef = useRef<HTMLSpanElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [bulkQueue, setBulkQueue] = useState<BoardCard[]>([]);
  const [access, setAccess] = useState<'idle' | 'pending' | 'done'>('idle');
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(new Set());
  }, [filter, expanded]);

  const data = board.data;
  const cards = useMemo(() => data?.columns.flatMap((column) => column.cards) ?? [], [data]);

  const openStage = useCallback((card: BoardCard, targetStageId: string | null) => {
    setDialog({ kind: 'stage', card, targetStageId });
  }, []);

  /** Deal-Pipeline 22 from the card menu: the deal's issued invoice opens the receipt dialog. */
  const recordReceipt = useCallback(
    async (card: BoardCard) => {
      try {
        const invoices = await queryClient.fetchQuery({
          queryKey: dealKeys.invoices(),
          queryFn: ({ signal }) => api<InvoicesResponse>('/deals/invoices', { signal }),
          staleTime: 15_000,
        });
        const row = invoices.items.find(
          (entry) => entry.dealId === card.id && (entry.status === 'issued' || entry.status === 'open'),
        );
        if (!row) {
          toast({ message: `Für ${card.contactName} ist keine gestellte Rechnung offen.` });
          return;
        }
        setDialog({ kind: 'received', card, row });
      } catch (error) {
        toast({ message: errorMessage(error) });
      }
    },
    [queryClient, toast],
  );

  const actions: CardActions = useMemo(
    () => ({
      onOpen: (card) => void navigate(`/deals/${card.id}`),
      onChangeStage: (card) => openStage(card, null),
      onLose: (card) => setDialog({ kind: 'lose', card, targetStageId: null }),
      onHistory: (card) => setDialog({ kind: 'history', card, targetStageId: null }),
      onOpenContact: (card) => void navigate(`/contacts/${card.contactId}`),
      onRecordReceipt: (card) => void recordReceipt(card),
    }),
    [navigate, openStage, recordReceipt],
  );

  function closeDialog() {
    setDialog(null);
    if (bulkQueue.length > 0) {
      const [next, ...rest] = bulkQueue;
      setBulkQueue(rest);
      if (next) setDialog({ kind: 'stage', card: next, targetStageId: null });
    }
  }

  function startBulkStage() {
    const queue = cards.filter((card) => selected.has(card.id));
    setSelected(new Set());
    const [first, ...rest] = queue;
    if (!first) return;
    setBulkQueue(rest);
    setDialog({ kind: 'stage', card: first, targetStageId: null });
  }

  async function requestWrite() {
    setAccess('pending');
    setAccessError(null);
    try {
      await requestDealsWriteAccess();
      setAccess('done');
    } catch (error) {
      setAccessError(errorMessage(error));
      setAccess('idle');
    }
  }

  const advisorName = filters.data?.advisors.find((user) => user.id === filter.advisorId)?.name ?? null;
  const propertyName = filters.data?.properties.find((entry) => entry.id === filter.propertyId)?.name ?? null;
  const partnerName =
    filters.data?.referrerPartners?.find((entry) => entry.id === filter.referrerPartnerId)?.name ?? null;
  const activeCount = activeFilterCount(filter);
  const noHits = Boolean(data && filter.q && data.summary.filteredDeals === 0);
  const emptyPipeline = Boolean(data && data.summary.deals === 0 && !data.summary.filtered && !filter.q);
  const readOnly = data?.permissions.readOnly ?? false;
  const canCreate = data?.permissions.canCreate ?? false;
  const columnEmptyText = noHits
    ? 'kein Treffer'
    : filter.overdueOnly
      ? 'alles in der Frist'
      : filter.gateOnly
        ? 'kein offenes Gate'
        : filter.advisorId ||
            filter.propertyId ||
            filter.referrerPartnerId ||
            filter.stageAgeOver ||
            filter.noNextStep
          ? filter.advisorId && advisorName
            ? `kein Deal von ${advisorName} in dieser Stage`
            : 'kein Deal im Filter'
          : 'leer';

  const stale =
    board.isError && board.dataUpdatedAt > 0
      ? `Stand ${formatTime(new Date(board.dataUpdatedAt))} · nicht aktuell`
      : null;
  const errorStatus = board.error instanceof ApiError ? board.error.status : null;

  const head = (
    <div className={pageStyles.head}>
      <span className={pageStyles.title}>Deals</span>
      <Segmented
        label="Pipeline"
        value={pipelineKind}
        items={[
          { value: 'property', label: 'Immobilie' },
          { value: 'financing', label: 'Finanzierung' },
        ]}
        onChange={onPipelineKindChange}
      />
      {property && !mobile ? (
        <>
          <FilterChip
            label={advisorName ? `Berater: ${advisorName}` : 'Alle Berater'}
            variant="dark"
            active={!filter.advisorId}
            onClick={() => setFlyoutOpen(true)}
            onRemove={
              filter.advisorId ? () => onFilterChange({ ...filter, advisorId: undefined }) : undefined
            }
          />
          <FilterChip
            label={propertyName ? `Objekt: ${propertyName}` : 'Objekt: alle'}
            variant="dark"
            active={Boolean(filter.propertyId)}
            onClick={() => setFlyoutOpen(true)}
            onRemove={
              filter.propertyId ? () => onFilterChange({ ...filter, propertyId: undefined }) : undefined
            }
          />
          {filter.referrerPartnerId ? (
            <FilterChip
              label={`Tippgeber-Partner: ${partnerName ?? '…'}`}
              variant="dark"
              active
              onClick={() => setFlyoutOpen(true)}
              onRemove={() => onFilterChange({ ...filter, referrerPartnerId: undefined })}
            />
          ) : null}
          <FilterChip
            label="Nur mit Gate-Warnung"
            variant="dark"
            active={Boolean(filter.gateOnly)}
            onClick={() => onFilterChange({ ...filter, gateOnly: filter.gateOnly ? undefined : true })}
            onRemove={filter.gateOnly ? () => onFilterChange({ ...filter, gateOnly: undefined }) : undefined}
          />
          {filter.overdueOnly ? (
            <FilterChip
              label="Sortierung: Stage-Alter"
              variant="dark"
              active
              onClick={() => onFilterChange({ ...filter, overdueOnly: undefined })}
              onRemove={() => onFilterChange({ ...filter, overdueOnly: undefined })}
            />
          ) : null}
          {filter.stageAgeOver ? (
            <FilterChip
              label={`älter als ${filter.stageAgeOver} Tage`}
              variant="dark"
              active
              onClick={() => onFilterChange({ ...filter, stageAgeOver: undefined })}
              onRemove={() => onFilterChange({ ...filter, stageAgeOver: undefined })}
            />
          ) : null}
          {filter.includeCancelled ? (
            <FilterChip
              label="Stornierte einblenden"
              variant="dark"
              active
              onClick={() => onFilterChange({ ...filter, includeCancelled: undefined })}
              onRemove={() => onFilterChange({ ...filter, includeCancelled: undefined })}
            />
          ) : null}
          <span ref={filterButtonRef} className={styles.filterAnchor}>
            <FilterChip
              label={activeCount > 0 ? `Filter · ${formatInteger(activeCount)} aktiv` : '+ Filter'}
              variant="dark"
              active={activeCount > 0}
              aria-haspopup="dialog"
              aria-expanded={flyoutOpen}
              onClick={() => setFlyoutOpen((value) => !value)}
            />
          </span>
        </>
      ) : null}
      <div className={pageStyles.headRight}>
        {property && data && !board.isError ? (
          <span className={pageStyles.summary}>
            {summaryText(data, filter, Boolean(filter.includeCancelled))}
          </span>
        ) : null}
        {stale ? <span className={pageStyles.staleNote}>{stale}</span> : null}
        {property ? (
          <SearchField
            label="Deals durchsuchen"
            placeholder="Name, Einheit, Objekt, Deal-Nummer"
            icon={false}
            className={pageStyles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        ) : null}
        {property && canCreate && !readOnly ? <Button onClick={onCreate}>Deal anlegen</Button> : null}
        {property && !mobile && data && !emptyPipeline ? (
          <Button variant="dark" size="small" onClick={() => onExpandedChange(!expanded)}>
            {expanded ? 'Spätphasen zusammenfassen' : 'Spätphasen aufklappen'}
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (!property) {
    return (
      <div className={pageStyles.page}>
        {head}
        <DealsTabs value="pipeline" onChange={onTabChange} />
        <FinancingBoard />
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      {head}
      <DealsTabs value="pipeline" onChange={onTabChange} />
      <FilterFlyout
        open={flyoutOpen}
        anchorRef={filterButtonRef}
        filter={filter}
        onClose={() => setFlyoutOpen(false)}
        onApply={(next) => {
          setFlyoutOpen(false);
          onFilterChange({ ...next, q: filter.q });
        }}
      />

      {board.isError ? (
        <Banner
          tone="danger"
          className={pageStyles.banner}
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={board.isFetching}
              onClick={() => void board.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Karten konnten nicht geladen werden{errorStatus ? ` · Fehler ${errorStatus}` : ''}. Die Spalten und
          Summen stehen fest; die Karten kommen beim nächsten Laden.
        </Banner>
      ) : null}

      {data?.permissions.locked ? (
        pipeline.data?.locked ? (
          <LockedPipeline info={pipeline.data} />
        ) : pipeline.isError ? (
          <Banner tone="danger">{errorMessage(pipeline.error)}</Banner>
        ) : (
          <BoardSkeleton mobile={mobile} />
        )
      ) : null}

      {data && !data.permissions.locked ? (
        <>
          {readOnly ? (
            <Banner
              tone="info"
              className={pageStyles.banner}
              actions={
                accessError ? (
                  <span className={pageStyles.staleNote}>{accessError}</span>
                ) : access === 'done' ? (
                  <span className={pageStyles.staleNote}>Ihr Antrag wurde gesendet.</span>
                ) : (
                  <Button
                    variant="secondary"
                    size="small"
                    loading={access === 'pending'}
                    onClick={() => void requestWrite()}
                  >
                    Schreibrecht anfragen
                  </Button>
                )
              }
            >
              Lesezugriff · Stages ändern darf nur der zuständige Berater.
            </Banner>
          ) : null}
          {filter.gateOnly && data.summary.filteredDeals > 0 ? (
            <Banner tone="warning" className={pageStyles.banner}>
              {formatInteger(data.summary.gateDeals)} Deals mit offenem Gate ·{' '}
              {formatInteger(data.summary.noNextStepDeals)} ohne nächsten Schritt
            </Banner>
          ) : null}
          {filter.overdueOnly && overdueBanner(data) ? (
            <Banner tone="danger" className={pageStyles.banner}>
              {overdueBanner(data)}
            </Banner>
          ) : null}
          {filter.includeCancelled && data.cancelledColumn ? (
            <Banner
              tone="info"
              className={pageStyles.banner}
              title="Storno steht in den Einstellungen, nicht hier"
            >
              Ein stornierter Deal verschwindet sofort für Setter und Closer. Sichtbar bleibt er für Admin und
              Backoffice — die Einheit ist frei, die Provisionspositionen sind als rückforderbar markiert.
              Setzen kann Storno nur ein Admin, über Einstellungen · Deals.
            </Banner>
          ) : null}
          {emptyPipeline ? (
            <div className={styles.emptyCard}>
              <div className={styles.emptyTitle}>Noch keine Deals in dieser Pipeline</div>
              <p className={styles.emptyText}>Ein Deal entsteht mit der ersten gebuchten Beratung.</p>
              <div className={styles.emptyActions}>
                <Button variant="dark" onClick={() => void navigate('/dialer')}>
                  Dialer starten
                </Button>
                <Button variant="secondary" onClick={onCreate}>
                  Deal manuell anlegen
                </Button>
              </div>
            </div>
          ) : null}
          {noHits ? (
            <div className={clsx(styles.emptyCard, styles.emptyCompact)}>
              <div className={styles.emptyTitle}>Kein Deal zu „{filter.q}“</div>
              <p className={styles.emptyText}>Gesucht wird in Name, Einheit, Objekt und Deal-Nummer.</p>
              <div className={styles.emptyActions}>
                <Button variant="dark" onClick={() => onFilterChange({ q: filter.q })}>
                  In allen Pipelines suchen
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void navigate(`/contacts?q=${encodeURIComponent(filter.q ?? '')}`)}
                >
                  In Kontakten suchen
                </Button>
              </div>
            </div>
          ) : null}
          <PipelineBoard
            data={data}
            expanded={expanded}
            showDeadline={Boolean(filter.overdueOnly)}
            emptyTexts={{ column: columnEmptyText }}
            actions={actions}
            onDrop={(card, stageId) => openStage(card, stageId)}
            onExpandLate={() => onExpandedChange(true)}
            selected={selected}
            onSelectionChange={setSelected}
            onBulkStage={startBulkStage}
            cancelledColumn={filter.includeCancelled ? data.cancelledColumn : null}
            mobile={mobile}
            dimmed={noHits || board.isError}
          />
        </>
      ) : null}

      {!data && board.isPending ? <BoardSkeleton mobile={mobile} /> : null}
      {!data && board.isError ? (
        <div className={styles.board} aria-hidden="true">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <div
              key={index}
              className={clsx(styles.column, index === 6 && styles.columnLate, styles.columnDimmed)}
            >
              <div className={styles.columnHead}>
                <span className={styles.columnName}>{index === 6 ? 'Spätphasen' : 'Stage'}</span>
                <span className={clsx(styles.columnCount, styles.columnCountZero)}>—</span>
              </div>
              <div className={styles.columnSub}>
                <span>—</span>
              </div>
              <div className={styles.columnEmpty}>nicht geladen</div>
            </div>
          ))}
        </div>
      ) : null}

      {dialog?.kind === 'stage' ? (
        <StageDialog
          open
          subject={{ id: dialog.card.id, contactName: dialog.card.contactName, line: dialog.card.unitLine }}
          targetStageId={dialog.targetStageId}
          onClose={closeDialog}
          onDone={(deal, nextStep) => {
            closeDialog();
            void invalidate(deal.id);
            toast({ message: `Stage gesetzt: ${deal.stageName}.` });
            if (nextStep === 'book_appointment')
              void navigate(`/contacts/${deal.contact.id}?editor=appointment`);
          }}
        />
      ) : null}
      {dialog?.kind === 'lose' ? (
        <LoseDialog
          open
          dealId={dialog.card.id}
          contactName={dialog.card.contactName}
          line={dialog.card.unitLine}
          unitNumber={
            dialog.card.unitLine.startsWith('WE') || dialog.card.unitLine.startsWith('TG')
              ? (dialog.card.unitLine.split(' · ')[0] ?? null)
              : null
          }
          onClose={closeDialog}
          onDone={(deal, nextStep) => {
            closeDialog();
            void invalidate(deal.id);
            toast({ message: `Deal verloren gesetzt: ${deal.contact.name}.` });
            if (nextStep === 'book_appointment')
              void navigate(`/contacts/${deal.contact.id}?editor=appointment`);
          }}
        />
      ) : null}
      {dialog?.kind === 'history' ? (
        <HistoryPanel
          open
          dealId={dialog.card.id}
          contactName={dialog.card.contactName}
          line={dialog.card.unitLine}
          onClose={closeDialog}
          onOpenDeal={() => void navigate(`/deals/${dialog.card.id}`)}
        />
      ) : null}
      {dialog?.kind === 'received' ? (
        <ReceivedDialog
          open
          row={dialog.row}
          responsibleLabel={handoverOf(pipeline.data)?.responsibleLabel ?? null}
          onClose={closeDialog}
          onDone={(result) => {
            closeDialog();
            void invalidate(result.deal.id);
            toast({ message: `Übergeben an ${result.ownerName}.` });
          }}
        />
      ) : null}
    </div>
  );
}
